import $ from 'jquery';
import Hotjar from '@hotjar/browser';
import './js/sentry-init.js';
import { hideLoading, showLoading, isIPhone } from './js/utils-module.js';
import * as fullScreenView from './js/fullScreenView.js';
import { captureEvents } from '../../packages/src/api/index.js';
import { initWaitingNotice } from './js/waitingNotice.js';
import { initGalleryViewController } from './js/imageList.js';
import { initActionPanel } from './js/actionPanel.js';
import { initLoc as initPageLocalization, t } from './js/localization.js';
import * as photoDC from './js/photoDS.js';

import 'bootstrap';
import { shareUrl } from './js/sharedAccount.js';
import { showTopBanner, showBottomBanner, onClickTopBanner, onClickBottomBanner } from './js/banner.js';
import './common.css';

const waitingNotice = initWaitingNotice();
const galleryVC = initGalleryViewController({ numberOfRows: 1 });
const actionPanel = initActionPanel();

let isSharingInProgress = false;

function OCCOAppPages() {
  let IMAGE_DATA = [];
  const self = this;
  let EVENT_DATA = null;

  this.init = async function () {
    showLoading();
    let result;
    try {
      result = await getEventData();
      await initPageLocalization();
    } catch (e) {
      alert('Inner error. Please try again later.');
      return;
    }
    hideLoading();

    const { opts } = result;

    if (isEmptyParsedOpts(opts)) {
      alert("Don't found event opts from event edit.");
      return;
    }

    // NOTE: needs for debugging options
    console.log('opts', opts);

    EVENT_DATA = result;
    updateStaticTexts();

    if (opts.webappBannerTopSrc) {
      showTopBanner({ imgSrc: opts.webappBannerTopSrc, url: opts.webappBannerTopLink });
      onClickTopBanner(() => {
        captureEvents([
          {
            EventName: 'BannerTopClick',
            EventSource: 'WebappSharedImages',
            EventType: 'report',
            EventId: EVENT_DATA.id,
            EventCode: EVENT_DATA.name,
            EventHref: window.location.href,
          },
        ]);
      });
    }

    if (opts.webappBannerBottomSrc) {
      showBottomBanner({ imgSrc: opts.webappBannerBottomSrc, url: opts.webappBannerBottomLink });
      onClickBottomBanner(() => {
        captureEvents([
          {
            EventName: 'BannerBottomClick',
            EventSource: 'WebappSharedImages',
            EventType: 'report',
            EventId: EVENT_DATA.id,
            EventCode: EVENT_DATA.name,
            EventHref: window.location.href,
          },
        ]);
      });
    }

    await getImageList();

    initBottomMenu({ isShowPrivacyLink: isNeedToShowPrivacy(opts) });

    fullScreenView.onDownloadBtn(self.downloadImage);
  };

  function isNeedToShowPrivacy(opts) {
    return !isEmptyParsedOpts(opts) && Boolean(opts.agreementText);
  }

  function isEmptyParsedOpts(parsedOpts) {
    return Object.keys(parsedOpts || {}).length === 0;
  }

  function initBottomMenu({ isShowPrivacyLink = false } = {}) {
    if (isShowPrivacyLink) {
      $('#privacy-link').show();
    }
  }

  function getImageList() {
    IMAGE_DATA = window.photoData;

    hideLoading();

    loadGallery();
  }

  function is360(photo) {
    return photo.image_type === '360_DEGREE';
  }

  function showFullScreen({ id }) {
    const index = IMAGE_DATA.findIndex((photo) => photo.id === id);
    fullScreenView.showItems(
      IMAGE_DATA.map((photo) =>
        photoDC.fullScreenVM(photo, {
          canDownload: true,
        })
      ),
      index
    );
  }

  function loadGallery() {
    const photosViewModel = IMAGE_DATA.map((photo) =>
      photoDC.viewModel(photo, {
        canDownload: true,
      })
    );

    galleryVC.render(photosViewModel, EVENT_DATA.empty_album_text);
    galleryVC.enableView();

    actionPanel.render(getActionPanelButtons());
    actionPanel.show();

    galleryVC.onClick(showFullScreen);
    galleryVC.onClickView(showFullScreen);

    galleryVC.onClickDownloadBtn(self.downloadImage);

    actionPanel.onClickBtn('shareLink', shareCurrentPage);
  }

  this.downloadImage = function ({ id }) {
    if (isIPhone()) {
      self.newShareImage({ id });
    } else {
      self.newDownloadImage({ id });
    }
  };

  this.newDownloadImage = function ({ id }) {
    waitingNotice.text(t('home-download-waiting-message'));
    waitingNotice.show();

    const photo = IMAGE_DATA.find(photoDC.isPhotoById(id));
    const urlAsArray = photo.full_path.split('/');
    const fileName = urlAsArray[urlAsArray.length - 1];

    captureEvents([
      {
        EventName: 'Download',
        EventSource: 'WebappSharedImages',
        EventType: 'report',
        EventId: EVENT_DATA.id,
        EventCode: EVENT_DATA.name,
        PartialAttendeeUid: getPartialAttendeeUid(),
        PhotoId: id,
        Count: 1,
        PipelineId: photo.image_process_id,
        EventHref: window.location.href,
      },
    ]);

    if (is360(photo)) {
      alert(
        'This is interactive content, best viewed within this album. \nYou can still download the raw file to share online or view later in apps that recognize 360 spherical format.'
      );
    }

    downloadFile(photo.full_path, fileName).finally(waitingNotice.hide);
  };

  this.newShareImage = function ({ id }) {
    const photo = IMAGE_DATA.find(photoDC.isPhotoById(id));
    shareFiles([photo]);
  };

  function downloadFile(url, fileName) {
    return fetch(url)
      .then((res) => res.blob())
      .then((res) => {
        const aElement = document.createElement('a');
        aElement.setAttribute('download', fileName);
        const href = URL.createObjectURL(res);
        aElement.href = href;
        aElement.setAttribute('target', '_blank');
        aElement.click();
        URL.revokeObjectURL(href);
        aElement.remove();
      })
      .catch((e) => {
        console.error('downloadFile error:', e);
      });
  }

  function shareFiles(photos) {
    shareFilesBySrcs(photos.map(photoDC.src));

    return captureEvents(
      photos.map(({ id, image_process_id }) => ({
        EventName: 'Download',
        EventSource: 'WebappSharedImages',
        EventType: 'report',
        EventId: EVENT_DATA.id,
        EventCode: EVENT_DATA.name,
        PartialAttendeeUid: getPartialAttendeeUid(),
        PhotoId: id,
        Count: 1,
        PipelineId: image_process_id,
        EventHref: window.location.href,
      }))
    );
  }

  function shareFilesExec(files) {
    if (isSharingInProgress) {
      console.warn('Share is already in progress.');
      return;
    }

    try {
      if (navigator.canShare && navigator.canShare({ files })) {
        isSharingInProgress = true;
        navigator
          .share({
            files,
            title: t('home-media-share-title'),
          })
          .then(() => console.log('Successful share.'))
          .catch((error) => console.log('Failed or canceled share.', error));
      } else {
        alert(t('home-share-files-unsupported-message'));
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.message.includes('cancellation of share')) {
        console.warn('Share was cancelled by the user or due to a system restriction:', error);
      } else if (error.name === 'InvalidStateError' && error.message.includes('share() is already in progress')) {
        console.warn('Share is already in progress. Please wait until it is complete.');
      } else {
        throw error;
      }
    } finally {
      isSharingInProgress = false;
    }
  }

  async function shareFilesBySrcs(srcs) {
    waitingNotice.bottomOffset('84px');
    waitingNotice.text(
      t('home-share-files-waiting-message')
    );
    waitingNotice.show();

    return downloadFiles(srcs).then(shareFilesExec).finally(waitingNotice.hide);

    function getNameByUrl(url) {
      const array = url.split('/');
      return array[array.length - 1];
    }

    async function downloadFiles(listOfSrc) {
      return new Promise(async (resolve, reject) => {
        const files = [];
        for (const src of listOfSrc) {
          try {
            const res = await fetch(src);
            const file = new File([await res.blob()], getNameByUrl(src), {
              type: res.headers.get('Content-Type'),
            });
            files.push(file);
          } catch (error) {
            reject(error);
          }
        }
        resolve(files);
      });
    }
  }

  async function getEventData() {
    return fetch(window.eventUrlJson).then((res) => res.json());
  }

  function getToken() {
    const url = new URL(window.location.href);
    const pathSegments = url.pathname.split('/');
    return pathSegments[pathSegments.length - 1].replace('.html', '');
  }

  function getPartialAttendeeUid() {
    return getToken().substring(0, 8);
  }

  function shareCurrentPage() {
    shareUrl({
      text: t('webapp-share-title'),
      url: window.location.href,
    });

    const timestreamEvents = [
      {
        EventSource: 'WebappSharedImages',
        EventName: 'SHARE_LINK',
        EventType: 'report',
        EventId: EVENT_DATA.id,
        EventCode: EVENT_DATA.name,
        Count: IMAGE_DATA.length,
        PartialAttendeeUid: getPartialAttendeeUid(),
        EventUrl: window.location.href,
      },
    ];

    IMAGE_DATA.forEach(({ id, image_process_id }) =>
      timestreamEvents.push({
        EventSource: 'WebappSharedImages',
        EventName: 'FacebookShare',
        EventType: 'report',
        EventId: EVENT_DATA.id,
        EventCode: EVENT_DATA.name,
        PartialAttendeeUid: getPartialAttendeeUid(),
        PhotoId: id,
        Count: 1,
        PipelineId: image_process_id,
        EventHref: window.location.href,
      })
    );

    return captureEvents(timestreamEvents);
  }

  function updateStaticTexts() {
    const selectedText = document.querySelector('.top-panel__selected-text');
    if (selectedText) {
      selectedText.textContent = t('home-selected-count-label', { count: 0 });
    }
  }

  function getActionPanelButtons() {
    return [{ id: 'shareLink', iconName: 'share', text: t('share-btn') }];
  }
}

const occoAppPages = new OCCOAppPages();

occoAppPages.init();

window.occoAppPages = occoAppPages;

if (process.env.WEBPAPP_SHARED_PAGE_HOTJAR_ID) {
  Hotjar.init(process.env.WEBPAPP_SHARED_PAGE_HOTJAR_ID, 6);
}
